import React, { useEffect, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
export function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Privacy Policy | IntelliEats';
  }, []);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="pt-32 pb-24 bg-white min-h-screen" data-id="element-1603" data-mp="1zzyuo70jll0a">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1604" data-mp="18913j7dtq8gx">
        <header className="mb-12" data-id="element-1605" data-mp="18alu6lx0p79i">
          <Link to="/" className="inline-flex items-center text-sm font-medium text-brand-600 hover:text-brand-700 mb-8 transition-colors" data-id="element-1606" data-mp="1m0fmxfbn8boi">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-1607" data-mp="1h7mg8lwm6t9s" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-6" data-id="element-1608" data-mp="tibpzycw4h9w">
            <div className="bg-brand-100 text-brand-600 p-3 rounded-2xl" data-id="element-1609" data-mp="oltnl5518v0q">
              <Shield className="w-8 h-8" data-id="element-1610" data-mp="30w97pihvu2t3" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tight" data-id="element-1611" data-mp="30vzc9fe2j3g0">
              Privacy Policy
            </h1>
          </div>
          <p className="text-slate-500 text-sm" data-id="element-1612" data-mp="3alixust61bek">Last updated: April 7, 2025</p>
        </header>

        <div className="space-y-10 text-slate-700 leading-relaxed" data-id="element-1613" data-mp="3flneciu2q64s">
          <section data-id="element-1614" data-mp="3fmyiijrflfwf">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-1615" data-mp="2hxn15o3ljjhs">
              1. Introduction
            </h2>
            <p data-id="element-1616" data-mp="2mvyew9x0ie5a">
              Welcome to IntelliEats ("we," "our," or "us"). We are committed to
              protecting your personal information and your right to privacy.
              This Privacy Policy explains how we collect, use, disclose, and
              safeguard your information when you use our mobile application
              IntelliEats (the "App").
            </p>
          </section>

          <section data-id="element-1617" data-mp="b65ru7dzw7bv">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-1618" data-mp="b5m7yl4sb8v7">
              2. Information We Collect
            </h2>
            <div className="space-y-4" data-id="element-1619" data-mp="289o3wuxmqc0q">
              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100" data-id="element-1620" data-mp="23903xjrfakud">
                <h3 className="font-bold text-slate-900 mb-2" data-id="element-1621" data-mp="2h7zr0mzaerof">
                  Camera & Photo Access
                </h3>
                <p className="m-0" data-id="element-1622" data-mp="2c8wx9yorc7vr">
                  We access your device camera and photo gallery solely to scan
                  ingredient labels. Images are processed locally on your device
                  for analysis and are not stored
                  permanently.
                </p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100" data-id="element-1623" data-mp="1ya5jyehpyq8o">
                <h3 className="font-bold text-slate-900 mb-2" data-id="element-1624" data-mp="1thm8bn2le56i">Usage Data</h3>
                <p className="m-0" data-id="element-1625" data-mp="15st5d6njyx2e">
                  We may collect anonymous usage data such as app interactions,
                  scan frequency, and feature usage to improve the App's
                  performance and user experience.
                </p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100" data-id="element-1626" data-mp="3cfik9clg541z">
                <h3 className="font-bold text-slate-900 mb-2" data-id="element-1627" data-mp="3ha69xes6s4i5">
                  Device Information
                </h3>
                <p className="m-0" data-id="element-1628" data-mp="38frlallrnhmd">
                  We may collect device type, operating system version, and
                  unique device identifiers for analytics and troubleshooting
                  purposes.
                </p>
              </div>
            </div>
          </section>

          <section data-id="element-1629" data-mp="3m751bpkgtxcn">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-1630" data-mp="3m7o767wucvab">
              3. How We Use Your Information
            </h2>
            <ul className="space-y-3 list-none p-0" data-id="element-1631" data-mp="3r4y408mk8986">
              <li className="flex items-start gap-3" data-id="element-1632" data-mp="2arznpqisqee9">
                <span className="w-2 h-2 bg-brand-500 rounded-full mt-2 shrink-0" data-id="element-1633" data-mp="2aqer7vcx8ldr"></span>
                <span data-id="element-1634" data-mp="2frvnteodhloj">
                  To provide and maintain the ingredient scanning and analysis
                  service
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-1635" data-mp="2q9hc3ro9mzqb">
                <span className="w-2 h-2 bg-brand-500 rounded-full mt-2 shrink-0" data-id="element-1636" data-mp="2q7wiq5dotmu1"></span>
                <span data-id="element-1637" data-mp="2m8npr2qh1szj">
                  To improve, personalize, and expand our App's features
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-1638" data-mp="3dzlj890nxhm2">
                <span className="w-2 h-2 bg-brand-500 rounded-full mt-2 shrink-0" data-id="element-1639" data-mp="3dz1ykkjmof4c"></span>
                <span data-id="element-1640" data-mp="39zb9qmdnw92g">To understand and analyze how you use our App</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-1641" data-mp="34xu8uqv6gmlv">
                <span className="w-2 h-2 bg-brand-500 rounded-full mt-2 shrink-0" data-id="element-1642" data-mp="2zzss4cl84zdk"></span>
                <span data-id="element-1643" data-mp="3026l8fwcg1ih">
                  To communicate with you for customer service and updates
                </span>
              </li>
            </ul>
          </section>

          <section data-id="element-1644" data-mp="1ojc9kaedi2mh">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-1645" data-mp="1on05fg7aj14d">
              4. Data Sharing & Third Parties
            </h2>
            <p data-id="element-1646" data-mp="1jfa6fcdfwjn1">
              We do not sell, trade, or rent your personal information to third
              parties. We may share anonymized, aggregated data with analytics
              partners to improve our services. Any third-party services used
              (such as analytics tools) are bound by their own privacy policies.
            </p>
          </section>

          <section data-id="element-1647" data-mp="1x9kkmzrt8veu">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-1648" data-mp="2261b40mhflv3">
              5. Data Security
            </h2>
            <p data-id="element-1649" data-mp="2754i3qm0w25p">
              We implement appropriate technical and organizational security
              measures to protect your personal information. However, no method
              of transmission over the Internet or electronic storage is 100%
              secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section data-id="element-1650" data-mp="hsk822vonb5u">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-1651" data-mp="9prwb4ukci34">
              6. Children's Privacy
            </h2>
            <p data-id="element-1652" data-mp="4rgpl2zkenp4">
              Our App is rated for ages 4+ and does not knowingly collect
              personal information from children under 13. If you believe we
              have collected information from a child under 13, please contact
              us immediately.
            </p>
          </section>

          <section data-id="element-1653" data-mp="snksloaoltrj">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-1654" data-mp="sn1l71inqnrr">
              7. Changes to This Policy
            </h2>
            <p data-id="element-1655" data-mp="nnoxyfnvjeon">
              We may update this Privacy Policy from time to time. We will
              notify you of any changes by posting the new Privacy Policy on
              this page and updating the "Last updated" date.
            </p>
          </section>

          <section className="bg-brand-50 rounded-2xl p-8 border border-brand-100" data-id="element-1656" data-mp="1bfx0sro0np9l">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-1657" data-mp="17ejvciou5o37">
              8. Contact Us
            </h2>
            <p className="mb-4" data-id="element-1658" data-mp="1q342s7vixcwc">
              If you have any questions about this Privacy Policy, please
              contact us:
            </p>
            <p className="font-medium text-slate-900" data-id="element-1659" data-mp="1h14ldeglk6sv">
              Email:{' '}
              <a href="mailto:intellieats@gmail.com" className="text-brand-600 hover:text-brand-700 transition-colors" data-id="element-1660" data-mp="1h3h2jpej6d4v">
                intellieats@gmail.com
              </a>
            </p>
            <p className="mt-2" data-id="element-1661" data-mp="1c2b9fqgyknut">
              Or use our{' '}
              <Link to="/contact" className="text-brand-600 hover:text-brand-700 font-medium transition-colors" data-id="element-1662" data-mp="1c3w6bmqqwivu">
                Contact Support
              </Link>{' '}
              page.
            </p>
          </section>
        </div>
      </div>
    </motion.div>;
}